import { ArrowLeftOutlined, ArrowRightOutlined, TableOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { statusLabel } from "@wms/domain";
import type { EntityMeta, Workflow } from "@wms/domain";
import { Alert, Button, Descriptions, Space, Steps, Tag, Typography } from "antd";
import type { ReactNode } from "react";
import { useState } from "react";
import { summaryRows } from "./actionGroups";
import { AssistantDocumentButton } from "./AssistantDocumentButton";
import { DocumentActions } from "./DocumentActions";
import type { Row } from "./EntityForm";
import { HelpHint } from "./HelpHint";
import { RefLabel } from "./RefSelect";
import type { CardStep } from "./stepwise";
import { neighbours, stateWords } from "./stepwise";

/**
 * ТЗ «Понятные шаги: UI/UX без потери функций» (30.10.2026), sections 5–8: the stepwise presentation of a card.
 * Three pieces the page lays around the one form and the child lists it already has: the strip with the context,
 * the block of the step (its question, its summary, its actions through the same DocumentActions) and the footer
 * («Назад» / «Далее» / «Полная карточка» / «Все действия» / the other sections). Nothing here posts anything of
 * its own: every button is a registered action with its stable code, and a step number is not a status (UX1-007).
 */

/** Reference fields that say whose document it is (UX1-006 (2)); the warehouse and `static` fields are always shown. */
const CONTEXT_REFS = new Set(["partner_id", "customer_id", "client_id", "owner_id", "company_id", "organization_id", "supplier_id", "recipient_id"]);

function statusOf(meta: EntityMeta, row: Row | undefined): string | null {
  const value = row?.status;
  if (value === null || value === undefined || value === "") return null;
  const field = meta.fields.find((f) => f.name === "status" && f.type === "enum");
  return field?.options?.find((o) => o.value === value)?.label ?? statusLabel(String(value));
}

function contextItems(meta: EntityMeta, row: Row): { label: string; node: ReactNode }[] {
  const items: { label: string; node: ReactNode }[] = [];
  for (const f of meta.fields) {
    if (f.hidden) continue;
    const value = row[f.name];
    if (value === null || value === undefined || value === "") continue;
    const wanted = !!f.static || f.name === "warehouse_id" || (f.type === "ref" && CONTEXT_REFS.has(f.name));
    if (!wanted) continue;
    items.push({ label: f.label, node: f.ref ? <RefLabel entity={f.ref} id={String(value)} /> : String(value) });
  }
  return items;
}

interface StripProps {
  meta: EntityMeta;
  row?: Row;
  isNew: boolean;
  steps: CardStep[];
  stepKey: string;
  onStep: (key: string) => void;
}

/** The strip of steps (done / current / future / not required / waiting for another employee) and the context line. */
export function StepStrip({ meta, row, isNew, steps, stepKey, onStep }: StripProps) {
  const index = Math.max(0, steps.findIndex((s) => s.key === stepKey));
  const status = statusOf(meta, row);
  const context = !isNew && row ? contextItems(meta, row) : [];
  return (
    <div data-testid="step-strip" style={{ marginBottom: 16 }}>
      <Steps
        size="small"
        responsive
        current={index}
        onChange={(i) => steps[i] && onStep(steps[i].key)}
        items={steps.map((s) => ({
          title: s.label,
          description:
            s.state === "input"
              ? undefined
              : (s.hint ?? (s.state === "skipped" ? "не требуется" : s.state === "waiting" ? "ожидает другого сотрудника" : undefined)),
          // the real state of the stage colours the strip; the step being looked at is outlined (CSS), so the two
          // never get mixed up — looking at a future step does not make it current
          status: s.state === "input" ? undefined : s.state === "done" ? "finish" : s.state === "current" || s.state === "waiting" ? "process" : "wait",
          className: [s.state === "waiting" ? "ant-steps-item-waiting" : "", s.key === stepKey ? "wms-step-viewed" : ""].join(" ").trim() || undefined,
        }))}
      />
      {(context.length > 0 || status) && (
        <div className="wms-step-context" data-testid="step-context" style={{ marginTop: 12 }}>
          {status && (
            <span>
              Статус: <b data-testid="step-status">{status}</b>
            </span>
          )}
          {context.map((c) => (
            <span key={c.label}>
              {c.label}: <b>{c.node}</b>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

interface BlockProps {
  entity: string;
  id: string;
  meta: EntityMeta;
  row?: Row;
  workflow?: Workflow | null;
  isNew: boolean;
  steps: CardStep[];
  step: CardStep;
  /** A section opened from the footer («Файлы», «История») instead of the step. */
  extraPane: { key: string; label: string } | null;
  onBackToStep: () => void;
  onDone: () => void;
  /** UXW-47 (option «Панель действий»): the grouped panel under the block carries the actions — not repeated here. */
  panel?: boolean;
}

/** The block of the step: its title and state, the one question or instruction, the summary, the actions of the stage. */
export function StepBlock({ entity, id, meta, row, workflow, isNew, steps, step, extraPane, onBackToStep, onDone, panel }: BlockProps) {
  if (extraPane) {
    return (
      <div data-testid="step-extra" style={{ marginBottom: 12 }}>
        <Space wrap align="center">
          <Typography.Title level={4} className="wms-step-title">
            {extraPane.label}
          </Typography.Title>
          <Button size="small" onClick={onBackToStep} data-testid="back-to-step">
            К шагу «{step.label}»
          </Button>
        </Space>
      </div>
    );
  }
  const index = steps.findIndex((s) => s.key === step.key);
  const { next } = neighbours(steps, step.key);
  const rec = workflow?.recommended ?? null;
  const live = step.state === "current" || step.state === "waiting";
  const recHere =
    !!rec &&
    ((rec.kind === "action" && !!rec.code && step.actions.includes(rec.code)) ||
      (rec.kind === "tab" && !!rec.key && step.tab === rec.key) ||
      (rec.kind === "wait" && live) ||
      (rec.kind === "done" && index === steps.length - 1));
  const firstReason = step.actions.map((code) => workflow?.actions[code]?.reason).find((r) => !!r) ?? null;

  let question: { type: "info" | "success" | "warning"; message: ReactNode; description?: ReactNode };
  if (step.kind === "part") {
    const last = !next;
    question = {
      type: "info",
      message: isNew ? (last ? "Проверьте данные и нажмите «Сохранить»" : "Заполните поля этого шага и нажмите «Далее»") : "Измените нужные поля и нажмите «Сохранить»",
      // the form itself shows `create_hint` on a new record — not repeated here
      description: step.hint ?? undefined,
    };
  } else if (step.state === "done") {
    question = { type: "success", message: "Этап выполнен", description: step.hint ?? undefined };
  } else if (step.state === "skipped") {
    question = { type: "info", message: "Не требуется", description: step.hint ?? "Этот этап для документа не нужен" };
  } else if (step.state === "waiting") {
    question = {
      type: "warning",
      message: `Сейчас выполняет сотрудник с правом «${step.hint ?? "…"}»`,
      description: rec?.kind === "wait" ? rec.instruction : "Дождитесь его или обратитесь к руководителю. Ваши права не меняются.",
    };
  } else if (step.state === "future") {
    question = { type: "info", message: "Будет доступен позже", description: firstReason ?? step.hint ?? "Станет доступен после предыдущих этапов" };
  } else if (recHere && rec) {
    question = {
      type: rec.kind === "done" ? "success" : "info",
      message: rec.kind === "action" ? `Следующий шаг: «${rec.label}»` : rec.kind === "tab" ? `Следующий шаг: ${rec.label.toLowerCase()}` : rec.label,
      description: rec.instruction,
    };
  } else {
    question = { type: "info", message: step.kind === "form" ? "Реквизиты документа" : "Текущий шаг", description: step.hint ?? (step.kind === "form" ? "Изменения шапки сохраняются кнопкой «Сохранить» внизу формы." : undefined) };
  }

  // UXW-47 / CW31-B02: a document of pieces and kilograms is summed per unit («160 шт; 2,5 кг»), never «162,5»; with the
  // panel the unit is named also when there is one
  const summary = summaryRows(workflow?.summary, !!panel);
  return (
    <div data-testid="step-block" data-step={step.key} data-step-state={step.state} style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
        <Typography.Title level={4} className="wms-step-title" data-testid="step-title">
          Шаг {index + 1} из {steps.length}: {step.label}
        </Typography.Title>
        {step.state !== "input" && <Tag data-testid="step-state">{stateWords(step.state)}</Tag>}
        {step.hint && step.kind !== "part" && <HelpHint subject={step.label} text={step.hint} />}
      </div>
      <Alert className="wms-step-question" data-testid="step-question" type={question.type} showIcon message={question.message} description={question.description} style={{ marginBottom: 12 }} />
      {!isNew && summary.length > 0 && (
        <Descriptions size="small" column={{ xs: 1, sm: 2, md: 4 }} bordered style={{ marginBottom: 12 }} data-testid="step-summary">
          {summary.map((r) => (
            <Descriptions.Item key={r.key} label={r.label}>
              {r.value}
            </Descriptions.Item>
          ))}
        </Descriptions>
      )}
      {!isNew && step.actions.length > 0 && !panel && (
        <div className="wms-step-actions" data-testid="step-actions" style={{ marginBottom: 12 }}>
          <DocumentActions
            entity={entity}
            id={id}
            status={String(row?.status ?? "")}
            actions={meta.actions}
            row={row as Record<string, unknown> | undefined}
            permission={meta.permission}
            workflow={workflow ?? null}
            only={step.actions}
            stepwise
            onDone={onDone}
          />
        </div>
      )}
      {next && step.kind !== "part" && (
        <Typography.Paragraph type="secondary" data-testid="step-next" style={{ marginBottom: 0 }}>
          Дальше: {next.label}
          {next.state === "waiting" ? ` — выполняет сотрудник с правом «${next.hint ?? "…"}»` : next.state === "skipped" ? " — не требуется" : next.state === "done" ? " — уже выполнено" : ""}
        </Typography.Paragraph>
      )}
    </div>
  );
}

interface FooterProps {
  entity: string;
  id: string;
  meta: EntityMeta;
  row?: Row;
  workflow?: Workflow | null;
  isNew: boolean;
  steps: CardStep[];
  step: CardStep;
  onStep: (key: string) => void;
  onFullCard: () => void;
  /** Sections of the card no step covers («Файлы», «История», «История товара»…), reachable from here (UX1-018). */
  sections: { key: string; label: string }[];
  extraPane: string | null;
  onSection: (key: string) => void;
  onDone: () => void;
}

/** «Назад» / «Далее» move along the strip only; «Полная карточка» and «Все действия» are always there (UX1-020). */
export function StepFooter({ entity, id, meta, row, workflow, isNew, steps, step, onStep, onFullCard, sections, extraPane, onSection, onDone }: FooterProps) {
  const { prev, next } = neighbours(steps, step.key);
  const [allOpen, setAllOpen] = useState(false);
  return (
    <div className="wms-step-footer" data-testid="step-footer" style={{ marginTop: 16 }}>
      <Space wrap>
        <Button icon={<ArrowLeftOutlined />} disabled={!prev} onClick={() => prev && onStep(prev.key)} data-testid="step-prev">
          Назад
        </Button>
        <Button type={step.kind === "part" && next ? "primary" : "default"} disabled={!next} onClick={() => next && onStep(next.key)} data-testid="step-forward">
          Далее <ArrowRightOutlined />
        </Button>
        <Button icon={<TableOutlined />} onClick={onFullCard} data-testid="full-card">
          Полная карточка
        </Button>
        {!isNew && meta.actions.length > 0 && (
          <Button icon={<UnorderedListOutlined />} onClick={() => setAllOpen((v) => !v)} data-testid="all-actions" aria-expanded={allOpen}>
            {allOpen ? "Скрыть все действия" : "Все действия"}
          </Button>
        )}
      </Space>
      {allOpen && !isNew && (
        <div data-testid="all-actions-panel" style={{ marginTop: 12 }}>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            Все действия документа — те же, что в полной карточке. Недоступные объясняют причину; опасные спрашивают подтверждение.
          </Typography.Paragraph>
          <Space wrap>
            <DocumentActions
              entity={entity}
              id={id}
              status={String(row?.status ?? "")}
              actions={meta.actions}
              row={row as Record<string, unknown> | undefined}
              permission={meta.permission}
              workflow={workflow ?? null}
              onDone={onDone}
            />
            <AssistantDocumentButton entity={entity} number={row?.number} />
          </Space>
        </div>
      )}
      {!isNew && sections.length > 0 && (
        <div className="wms-step-sections" style={{ marginTop: 12 }} data-testid="step-sections">
          <Typography.Text type="secondary">Другие разделы:</Typography.Text>
          {sections.map((s) => (
            <Button key={s.key} size="small" type={extraPane === s.key ? "primary" : "default"} onClick={() => onSection(s.key)} data-testid={`section-${s.key}`}>
              {s.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
